import './StaffUsersView.css'

import { useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../../store'

import { Button } from '../../../components/Button'
import { DataTable } from '../../../components/DataTable'
import { EmptyState } from '../../../components/EmptyState'
import { FilterBar } from '../../../components/FilterBar'
import { SelectField } from '../../../components/SelectField'
import { Tag } from '../../../components/Tag'
import { TextField } from '../../../components/TextField'

const sortItems = [
  { value: 'createdAt', text: 'Created' },
  { value: 'email', text: 'Email' },
  { value: 'subscriptionTier', text: 'Subscription' },
]

const testMatchModeItems = [
  { value: 'disabled', text: 'Off (real matches only)' },
  { value: 'onlySimulated', text: 'Simulated only' },
  { value: 'simulatedAndReal', text: 'Simulated + real' },
]

export function StaffUsersView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const users = useAtomValue(staff.staffUsersAtom)
  const total = useAtomValue(staff.staffUsersTotalAtom)
  const isLoading = useAtomValue(staff.staffUsersIsLoadingAtom)
  const error = useAtomValue(staff.staffUsersErrorAtom)
  const query = useAtomValue(staff.staffUserQueryAtom)
  const selected = useAtomValue(staff.staffSelectedUserAtom)
  const selectedLoading = useAtomValue(staff.staffSelectedUserIsLoadingAtom)
  const actionLoading = useAtomValue(staff.staffActionIsLoadingAtom)

  const setQuery = useSetAtom(staff.setStaffUserQueryAtom)
  const fetchUsers = useSetAtom(staff.fetchStaffUsersAtom)
  const fetchUserDetail = useSetAtom(staff.fetchStaffUserDetailAtom)
  const banUser = useSetAtom(staff.banStaffUserAtom)
  const unbanUser = useSetAtom(staff.unbanStaffUserAtom)
  const setTestMatchMode = useSetAtom(staff.setStaffUserTestMatchModeAtom)
  const setSelected = useSetAtom(staff.staffSelectedUserAtom)

  const canRead = staff.hasStaffPermission(session, staff.Permission.ModerationUsersRead)
  const canWrite = staff.hasStaffPermission(session, staff.Permission.ModerationUsersWrite)

  const [searchDraft, setSearchDraft] = useState(query.q)

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchDraft !== query.q) setQuery({ q: searchDraft })
    }, 300)
    return () => window.clearTimeout(handle)
  }, [searchDraft, query.q, setQuery])

  useEffect(() => {
    if (canRead) fetchUsers()
  }, [canRead, fetchUsers, query])

  const columns = useMemo(
    () => [
      {
        id: 'email',
        header: 'Email',
        render: (user: staff.StaffUserRow) => (
          <div className="staff-users__primary">
            <span className="staff-users__email">{user.email}</span>
            {user.name && <span className="staff-users__name">{user.name}</span>}
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        render: (user: staff.StaffUserRow) => (
          <div className="staff-users__tags">
            <Tag color={user.isActive ? 'teal' : 'red'}>
              {user.isActive ? 'active' : 'inactive'}
            </Tag>
            {!user.isVerified && <Tag color="amber">unverified</Tag>}
            {user.rejectionType && <Tag color="red">{user.rejectionType}</Tag>}
            {user.testMatchMode === 'onlySimulated' && <Tag color="amber">sim only</Tag>}
            {user.testMatchMode === 'simulatedAndReal' && <Tag color="teal">sim + real</Tag>}
          </div>
        ),
      },
      {
        id: 'subscription',
        header: 'Subscription',
        render: (user: staff.StaffUserRow) =>
          `${user.subscriptionTier || 'free'} · ${user.subscriptionStatus || '—'}`,
      },
      {
        id: 'location',
        header: 'Location',
        render: (user: staff.StaffUserRow) => user.location || '—',
      },
      {
        id: 'photos',
        header: 'Photos',
        render: (user: staff.StaffUserRow) =>
          `${user.photoApproved}a / ${user.photoPending}p / ${user.photoRejected}r`,
      },
      {
        id: 'reports',
        header: 'Reports',
        render: (user: staff.StaffUserRow) => user.openReports,
      },
      {
        id: 'created',
        header: 'Created',
        render: (user: staff.StaffUserRow) => formatDate(user.createdAt),
      },
    ],
    [],
  )

  if (!canRead) {
    return (
      <EmptyState
        title="No user access"
        description="Your role does not include moderation.users.read."
      />
    )
  }

  return (
    <div className="staff-users">
      <FilterBar>
        <div className="staff-users__search">
          <TextField
            label="Search members"
            kind="search"
            placeholder="Email, name, location, id…"
            value={searchDraft}
            onChange={setSearchDraft}
          />
        </div>
        <div className="staff-users__sort">
          <SelectField
            label="Sort"
            items={sortItems}
            value={query.sort}
            onChange={(sort) => setQuery({ sort })}
          />
        </div>
        <Button
          kind="secondary"
          text={isLoading ? 'Loading…' : 'Refresh'}
          onClick={() => fetchUsers()}
          disabled={isLoading}
        />
      </FilterBar>

      <p className="staff-users__count">
        {isLoading ? 'Loading…' : `${total.toLocaleString()} member${total === 1 ? '' : 's'}`}
      </p>

      {error && (
        <EmptyState
          title="Failed to load users"
          description={
            error === 'forbidden'
              ? 'Forbidden for this role.'
              : error === 'network'
                ? 'Network error.'
                : 'Request failed.'
          }
          actions={<Button kind="secondary" text="Retry" onClick={() => fetchUsers()} />}
        />
      )}

      {!error && (
        <div className="staff-users__layout">
          <DataTable
            columns={columns}
            rows={users}
            getRowKey={(user) => user.id}
            emptyMessage="No members match"
            onRowClick={(user) => fetchUserDetail(user.id)}
          />

          <aside className="staff-users__detail">
            {!selected && !selectedLoading && (
              <EmptyState
                title="Select a member"
                description="Click a row to inspect profile, photos, and reports."
              />
            )}
            {selectedLoading && <p className="staff-users__detail-loading">Loading member…</p>}
            {selected && !selectedLoading && (
              <div className="staff-users__detail-body">
                <h2 className="staff-users__detail-title">{selected.user.email}</h2>
                <p className="staff-users__detail-meta">
                  {selected.user.name || 'No name'} · {selected.user.location || 'No location'}
                </p>
                <div className="staff-users__tags">
                  <Tag color={selected.user.isActive ? 'teal' : 'red'}>
                    {selected.user.isActive ? 'active' : 'inactive'}
                  </Tag>
                  <Tag color={selected.user.isVerified ? 'violet' : 'amber'}>
                    {selected.user.isVerified ? 'verified' : 'unverified'}
                  </Tag>
                  {selected.user.rejectionType && (
                    <Tag color="red">{selected.user.rejectionType}</Tag>
                  )}
                  {selected.user.testMatchMode === 'onlySimulated' && (
                    <Tag color="amber">sim only</Tag>
                  )}
                  {selected.user.testMatchMode === 'simulatedAndReal' && (
                    <Tag color="teal">sim + real</Tag>
                  )}
                </div>

                <dl className="staff-users__facts">
                  <div>
                    <dt>User id</dt>
                    <dd>{selected.user.id}</dd>
                  </div>
                  <div>
                    <dt>Test match mode</dt>
                    <dd>
                      {canWrite ? (
                        <SelectField
                          label="Mode"
                          items={testMatchModeItems}
                          value={selected.user.testMatchMode || 'disabled'}
                          onChange={(mode) => {
                            void setTestMatchMode({
                              userId: selected.user.id,
                              testMatchMode: mode as staff.TestMatchMode,
                            })
                          }}
                        />
                      ) : (
                        selected.user.testMatchMode || 'disabled'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Photos</dt>
                    <dd>
                      {selected.user.photoApproved} approved · {selected.user.photoPending} pending
                      · {selected.user.photoRejected} rejected
                    </dd>
                  </div>
                  <div>
                    <dt>Open reports</dt>
                    <dd>{selected.user.openReports}</dd>
                  </div>
                  {selected.user.rejectionReason && (
                    <div>
                      <dt>Rejection reason</dt>
                      <dd>{selected.user.rejectionReason}</dd>
                    </div>
                  )}
                </dl>

                {selected.reports.length > 0 && (
                  <section className="staff-users__section">
                    <h3 className="staff-users__section-title">Reports</h3>
                    <ul className="staff-users__report-list">
                      {selected.reports.map((report) => (
                        <li key={report.id} className="staff-users__report">
                          <strong>{report.reason}</strong>
                          <span>
                            {report.status} · {formatDate(report.createdAt)}
                          </span>
                          {report.details && <p>{report.details}</p>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div className="staff-users__detail-actions">
                  <Button kind="ghost" text="Close" onClick={() => setSelected(null)} />
                  {canWrite && selected.user.isActive && (
                    <Button
                      kind="destructive"
                      text={actionLoading ? 'Banning…' : 'Ban member'}
                      disabled={actionLoading}
                      onClick={() =>
                        banUser({
                          userId: selected.user.id,
                          reason: 'Banned by staff',
                        })
                      }
                    />
                  )}
                  {canWrite && !selected.user.isActive && (
                    <Button
                      kind="primary"
                      text={actionLoading ? 'Unbanning…' : 'Unban member'}
                      disabled={actionLoading}
                      onClick={() => unbanUser(selected.user.id)}
                    />
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  )
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}
